import { getWorkspaceAuthSession } from '@amglobal/auth-shared'
import type { MeSessionSnapshot } from '@/features/auth/api/auth-types'

const TENANT_LABEL_KEY = 'amglobal_workspace_tenant_label'

/** Last known tenant label from a successful `/auth/me/session` response. */
export function readCachedWorkspaceTenantLabel(): string | null {
  if (typeof localStorage === 'undefined') return null
  const raw = localStorage.getItem(TENANT_LABEL_KEY)?.trim()
  return raw && raw.length > 0 ? raw : null
}

export function cacheWorkspaceTenantLabel(
  tenantName: string | null | undefined,
  tenantId: string | undefined,
): void {
  if (typeof localStorage === 'undefined') return
  const label = tenantName?.trim() || tenantId?.trim()
  if (!label) return
  localStorage.setItem(TENANT_LABEL_KEY, label)
}

export function clearCachedWorkspaceTenantLabel(): void {
  if (typeof localStorage === 'undefined') return
  localStorage.removeItem(TENANT_LABEL_KEY)
}

/** Partial session for header/nav while `/auth/me/session` is loading or after a transient failure. */
export function partialSessionFromPersistedAuth(): MeSessionSnapshot | undefined {
  const persisted = getWorkspaceAuthSession()
  if (!persisted) return undefined

  return {
    catalogVersion: 0,
    sessionVersion: persisted.user.sessionVersion,
    tenantId: '',
    tenantName: readCachedWorkspaceTenantLabel(),
    displayName: persisted.user.email.trim(),
    roles: [],
    capabilities: persisted.user.capabilities,
    navOverrides: null,
  }
}

export function resolveSessionDisplayName(snapshot: MeSessionSnapshot | undefined): string {
  const fromApi = snapshot?.displayName?.trim()
  if (fromApi) return fromApi
  const fromPersisted = getWorkspaceAuthSession()?.user.email.trim()
  return fromPersisted || 'User'
}

export function resolveSessionRoleLabel(snapshot: MeSessionSnapshot | undefined): string | null {
  const role = snapshot?.roles[0]?.trim()
  return role && role.length > 0 ? role : null
}

export function resolveSessionTenantLabel(snapshot: MeSessionSnapshot | undefined): string {
  const fromApi = snapshot?.tenantName?.trim()
  if (fromApi) return fromApi
  const fromId = snapshot?.tenantId?.trim()
  if (fromId) return fromId
  return readCachedWorkspaceTenantLabel() ?? '—'
}
